/* eslint-disable camelcase */
import * as marietta from '../..';
import { BUILDING_TYPE_MANSION, ITEM_MODE_KEY, ITEM_MODE_NO, SITE_COLOR, SITE_INPUT_MODE } from '../Constants';
import DocumentCollection from '../collection/DocumentCollection';
import { LinkType, PointJsonType, RemarksType } from '../collection/ItemInfoCollection';
import MasterItemCollection from '../collection/MasterItemCollection';
import { AuxiliaryData } from './LiDAR/AuxiliaryInterface';
import { MarkItemInterface } from './LiDAR/MarkInterface';
import { AuxiliaryUtil } from '../manager';
import AuxiliaryConstructionCollection from '../collection/AuxiliaryConstructionCollection';
import { ExtrasType } from '../../view3d/webcad/WebCadDefines';

export type ApplicationData = {
  name: string;
  version: string;
  customize: string;
}

export type CommonItemInfoData = {
  index: number;
  id: string;
  f: number;
  shape?: PointJsonType[];
  guid: string
}

export type SiteInfoData<T> = {
  mode: number;
  area: number;
  direction: number;
  shape: T[];
  guid: string;
}
export type RoomMaterialData = {
  ceiling:string,
  wall:string,
  kosi:string,
  habaki:string,
  floor:string,
}
export interface RoomInfoData<T> extends CommonItemInfoData {
  floor_end_item_info:MasterItemCollection;
  ceiling_end_item_info:MasterItemCollection;
  floor_white_item_info:MasterItemCollection;
  ceiling_white_item_info:MasterItemCollection;
  color: string;
  design_color: string;
  design_type: string;
  text: T;
  material: RoomMaterialData;
  ceiling_hang_height:number;
  ceiling_documents:DocumentCollection[];
  floor_documents:DocumentCollection[];
  habaki_height:number;
}

export interface WallInfoData extends CommonItemInfoData { }

export interface StairInfoData extends CommonItemInfoData {
  width: number;
  depth: number;
  x: number;
  y: number;
  rotation: number;
  is_reverse: boolean;
  idthreed: string;
}

export type RoofInfoData<T> = {
  noki_de: number;
  tsuma_de: number;
  items: T[];
}

export interface CommonInfoData extends CommonItemInfoData {
  width: number;
  depth: number;
  x: number;
  y: number;
  rotation: number;
  is_reverse: boolean;
  idthreed: string;
  width_mm?: string;
  use_mm?: boolean;
  auto_height?: boolean;
  z?: number;
  height?: number;
  window_frame?: number;
  type?: number;
  is_wall_item?:boolean;
  is_ceiling_item?:boolean;
  text:string;
  end_item_info:MasterItemCollection;
  white_item_info:MasterItemCollection;
  material: RoomMaterialData;
  documents:DocumentCollection[];
}

export interface FreeTextInfoData extends CommonItemInfoData {
  x: number;
  y: number;
  z: number;
  rotation: number;
  transformMatrix: number[];
  width: number;
  height: number;
  text: string;
  text_color: string;
  font_size: number;
  is_bold: boolean;
  is_underline: boolean;
  is_italic: boolean;
  is_strike_through: boolean;
  text_alignment: string;
  link: LinkType[];
  file_id?: string,
  after_link?: LinkType[];
  pin_id?: string;
  is_register_img?: boolean;
  auxiliaryData?: AuxiliaryData;
  intersectItem?: {uid: string, type: ExtrasType, linkedAt?: number};
  remarks?: RemarksType[];
}

export type PlanInfoData = {
  module: number;
  grid: number;
  building_type: number;
  dimension_correction?: number;
  grade: string;
  outer_wall_width?: number
  model_param?: any;
  site?: SiteInfoData<{ index: number; color: string; base?: number; point: PointJsonType[] }>;
  room?: RoomInfoData<{ x: number; y: number; area_visible: number; auto_layout: number; area_use_jyou: number; visible: number; name: string, habaki_height:number }>[];
  wall?: WallInfoData[];
  stair?: StairInfoData[];
  fitting?: CommonInfoData[];
  equipment?: CommonInfoData[];
  furniture?: CommonInfoData[];
  exterior?: CommonInfoData[];
  roof?: RoofInfoData<CommonItemInfoData & { type: number; direction?: number; }>;
  text?: FreeTextInfoData[];
  column?: CommonInfoData[];
  beam?: CommonInfoData[];
  quotation?: QuotationData[];
  mark?: MarkItemInterface[];
}

export type WebcadJsonData = {
  application: ApplicationData;
  plan: PlanInfoData
}

export type QuotationData = {
  is_quote:boolean;
  name: string;
  guid: string;
  floor: number;
  width: number;
  depth: number;
  height: number;
  area: number;
  z: number;
  isCeiling: number;
  unit:string;
  maker:string;
  part_number:string;
  item_name:string;
  item_sub_category:string;
  unit_price:string;
  price:string;
  item_set_date:string;
  note:string;
  room_name:string;
  end_item_info:MasterItemCollection;
  white_item_info:MasterItemCollection;
  documents:DocumentCollection[];
  auxiliary_constructions:AuxiliaryConstructionCollection[];
}

export type QuotationDataForSite = {
  item_id:string;     //アイテムID
  guid:string;     //guid
  order:number;       //ソート順
  room_name:string;   //部屋名
  part_number:string; //品番
  item_name:string;   //商品名
  quantity:number;    //数量
  unit:string;        //単位
  unit_price:number;  //単価
  price:number;
  note:string;        //備考
  maker:string;        //メーカー名
  item_sub_category:string;      //分類
  construction_code:string;   //共通工事コード
  documents:DocumentCollection[];        //備考
  auxiliary_constructions:AuxiliaryConstructionCollection[];  //付帯工事一覧
  webcad_item_name: string; // アイテム名
}

export default class WebcadJSONExporter {
  /**
     * 保存データの作成
     */
  static retJSON: WebcadJsonData;

  static firstNode: Element;

  static planNode: Element;

  static createWebCADData(): string {
    this.retJSON = <WebcadJsonData>{};
    // アプリケーション
    this.retJSON.application = this.applicationJSON();

    // プラン情報
    this.retJSON.plan = this.planInfoJSON();

    // 3D用パラメータ（）

    this.retJSON.plan.model_param = this.modelParamInfoJson();

    // 敷地
    this.retJSON.plan.site = this.siteInfoJSON();

    // 間取
    this.retJSON.plan.room = this.roomInfoJSON();

    // 壁
    this.retJSON.plan.wall = this.wallInfoJSON();

    // 階段
    this.retJSON.plan.stair = this.stairInfoJSON();

    // 建具
    this.retJSON.plan.fitting = this.commonInfoJSON('fitting', ITEM_MODE_NO.MODE_FITTING_NO);

    // 住設
    this.retJSON.plan.equipment = this.commonInfoJSON('equipment', ITEM_MODE_NO.MODE_EQUIPMENT_NO);

    // 家具
    this.retJSON.plan.furniture = this.commonInfoJSON('furniture', ITEM_MODE_NO.MODE_FURNITURE_NO);

    // エクステリア
    this.retJSON.plan.exterior = this.commonInfoJSON('exterior', ITEM_MODE_NO.MODE_EXTERIOR_NO);

    // 屋根
    this.retJSON.plan.roof = this.roofInfoJSON();

    // テキストアイテム
    this.retJSON.plan.text = this.freeTextInfoJSON();

    // 柱
    this.retJSON.plan.column = this.columnInfoJSON();

    // 梁
    this.retJSON.plan.beam = this.beamInfoJSON();

    //見積
    this.retJSON.plan.quotation = this.quotationInfoJSON();

    // マークアイテム
    this.retJSON.plan.mark = this.markInfoJSON();

    return JSON.stringify(this.retJSON);
  }

  /**
  * アプリ情報JSON
 */
  static applicationJSON(): ApplicationData {
    return {
      name: marietta.webcad.WebCADGlobal.APP_NAME,
      version: marietta.webcad.WebCADGlobal.APP_VERSION,
      customize: marietta.webcad.WebCADGlobal.APP_CUSTOM,
    };
  }

  /**
   * プラン情報JSON
 */
  static planInfoJSON(): PlanInfoData {
    let gradeId = null;
    if (marietta.webcad.WebCADGlobal.webcad2Dmanager.grade) {
      gradeId = marietta.webcad.WebCADGlobal.webcad2Dmanager.grade.id;
    }
    return {
      module: marietta.webcad.WebCADGlobal.webcad2Dmanager.modulePitch,
      grid: marietta.webcad.WebCADGlobal.webcad2Dmanager.snapUnit,
      building_type: marietta.webcad.WebCADGlobal.webcad2Dmanager.buildingType,
      dimension_correction: marietta.webcad.WebCADGlobal.moduleManager.dimensionCorrection,
      grade: gradeId,
      outer_wall_width: marietta.webcad.WebCADGlobal.webcad2Dmanager.outerWallThickness,
    };
  }

  /**
  * 3D用パラメータJson
  */
  static modelParamInfoJson(): any {
    if (marietta.webcad.WebCADGlobal.webcad2Dmanager.modelParam) {
      const param: marietta.webcad.collection.ModelParamCollection = marietta.webcad.WebCADGlobal.webcad2Dmanager.modelParam;
      return {
        option: param.option,
        height: param.height,
        auto: param.auto,
        material: param.material,
      };
    }
    return null;
  }

  /**
  * 敷地情報JSON
  */
  static siteInfoJSON<Shape extends { index: number; color: string; base?: number; point: PointJsonType[]; guid: string }>(): SiteInfoData<Shape> {
    const tarLayer: marietta.webcad.layer.SiteLayer = marietta.webcad.WebCADGlobal.layerGroup.getTypeLayerInFloorLayer(1, ITEM_MODE_NO.MODE_SITE_NO);
    const itemList: marietta.webcad.item.Site[] = tarLayer.getAllChildren() as marietta.webcad.item.Site[];
    let info: marietta.webcad.collection.ItemInfoCollection;

    // 敷地一覧
    if (itemList.length > 0) {
      const siteNode: SiteInfoData<Shape> = <SiteInfoData<Shape>>{};
      siteNode.mode = tarLayer.mode;
      siteNode.area = tarLayer.getAreaAll();
      siteNode.direction = marietta.webcad.WebCADGlobal.siteCompass.rotation;
      siteNode.shape = [];
      for (let i = 0, len = itemList.length; i < len; i += 1) {
        info = itemList[i].itemInfo;
        // eslint-disable-next-line no-continue
        if (!(itemList[i] instanceof marietta.webcad.item.Site)) continue;
        // アイテム
        const itemNode: Shape = <Shape>{};
        itemNode.index = (i + 1);
        itemNode.color = String(SITE_COLOR);
        itemNode.guid = info.guid;
        if (tarLayer.mode === SITE_INPUT_MODE.SITE_DETAILED_INPUT_MODE) {
          itemNode.base = info.baseNo;
        }
        itemNode.point = info.getPointJSON(ITEM_MODE_KEY.MODE_SITE_KEY);
        siteNode.shape.push(itemNode);
      }
      return siteNode;
    }
    return null;
  }

  /**
  * 部屋情報ノード
  */
  static roomInfoJSON<T extends { x: number; y: number; area_visible: number; ceiling_height_visible: number; auto_layout: number; area_use_jyou: number; visible: number; name: string; habaki_height:number }>(list: marietta.webcad.item.Room[] = null): RoomInfoData<T>[] {
    let itemList: marietta.webcad.item.Room[] = marietta.webcad.WebCADGlobal.itemManager.getItemListByMode(ITEM_MODE_NO.MODE_ROOMDOWN1_NO) as marietta.webcad.item.Room[];
    itemList = itemList.concat(marietta.webcad.WebCADGlobal.itemManager.getItemListByMode(ITEM_MODE_NO.MODE_ROOMDOWN2_NO) as marietta.webcad.item.Room[]);
    itemList = itemList.concat(marietta.webcad.WebCADGlobal.itemManager.getItemListByMode(ITEM_MODE_NO.MODE_ROOM_NO) as marietta.webcad.item.Room[]);
    if (list != null) {
      itemList = list;
    }

    // 部屋一覧
    let textSavePoint: marietta.core.Point;
    const roomNode: RoomInfoData<T>[] = [];
    for (let i: number = 0, len = itemList.length; i < len; i += 1) {
      const info: marietta.webcad.collection.ItemInfoCollection = itemList[i].itemInfo;
      // 部屋面積ほぼゼロチェック
      if (marietta.webcad.util.WebCADUtil.isAreaZero(info.dotList)) continue;
      // アイテム
      const itemNode: RoomInfoData<T> = <RoomInfoData<T>>{};
      itemNode.index = (i + 1);
      itemNode.id = info.id;
      itemNode.f = info.floor;
      itemNode.color = String(marietta.webcad.util.WebCADUtil.convertNum16To10(info.color));
      itemNode.design_color = String(info.designColor ? marietta.webcad.util.WebCADUtil.convertNum16To10(info.designColor) : '');
      itemNode.design_type = info.design_type ? info.design_type : '';
      itemNode.guid = info.guid;
      if (info.ceiling_end_item_info) {
        itemNode.ceiling_end_item_info = info.ceiling_end_item_info;
      }
      if (info.floor_end_item_info) {
        itemNode.floor_end_item_info = info.floor_end_item_info;
      }
      if (info.ceiling_white_item_info) {
        itemNode.ceiling_white_item_info = info.ceiling_white_item_info;
      }
      if (info.floor_white_item_info) {
        itemNode.floor_white_item_info = info.floor_white_item_info;
      }

      const materialNode: RoomMaterialData = <RoomMaterialData>{};
      materialNode.ceiling = info.material.ceiling ? info.material.ceiling : null;
      materialNode.wall = info.material.wall ? info.material.wall : null;
      materialNode.kosi = info.material.kosi ? info.material.kosi : null;
      materialNode.habaki = info.material.habaki ? info.material.habaki : null;
      materialNode.floor = info.material.floor ? info.material.floor : null;
      itemNode.material = materialNode;
      itemNode.ceiling_hang_height = info.ceiling_hang_height ? info.ceiling_hang_height : 0;
      itemNode.floor_documents = info.floor_documents ?? [];
      itemNode.ceiling_documents = info.ceiling_documents ?? [];
      itemNode.habaki_height = info.habaki_height ? info.habaki_height:0;
      // テキスト
      // let textInfo: marietta.webcad.item.parts.RoomText = itemList[i].overlayText;
      const textInfo: marietta.webcad.item.parts.RoomText = itemList[i].overlayText;
      textSavePoint = itemList[i].overlayText.getSavePoint();
      const textNode: T = <T>{};
      textNode.x = textSavePoint.x;
      textNode.y = textSavePoint.y;
      textNode.area_visible = Number(textInfo.areaVisible);
      textNode.ceiling_height_visible = Number(textInfo.ceilingHeightVisible);
      textNode.auto_layout = Number(textInfo.autoLayout);
      textNode.area_use_jyou = Number(textInfo.useJyou);
      textNode.visible = Number(textInfo.visible);
      textNode.name = textInfo.message;


      // シェイプ
      itemNode.text = textNode;

      itemNode.shape = info.getPointJSON(ITEM_MODE_KEY.MODE_ROOM_KEY); // シェイプ
      roomNode.push(itemNode);
    }
    return roomNode;
  }

  /**
   * 壁
   * @param list
   * @returns
   */
  static wallInfoJSON(list: marietta.webcad.item.WallObject[] = null): WallInfoData[] {
    let itemList: marietta.webcad.item.WallObject[] = <marietta.webcad.item.WallObject[]>marietta.webcad.WebCADGlobal.itemManager.getItemListByMode(ITEM_MODE_NO.MODE_WALL_NO);
    if (list != null) itemList = list;
    const wallNode: WallInfoData[] = [];
    for (let i: number = 0, len = itemList.length; i < len; i += 1) {
      const { itemInfo } = itemList[i];
      // アイテム
      const itemNode: WallInfoData = <WallInfoData>{};
      itemNode.index = i + 1;
      itemNode.id = itemInfo.id;
      itemNode.f = itemInfo.floor;
      itemNode.guid = itemInfo.guid;
      itemNode.shape = itemInfo.getPointJSON(ITEM_MODE_KEY.MODE_WALL_KEY);
      wallNode.push(itemNode);
    }
    return wallNode;
  }

  /**
  * 各種情報ノード
  */
  static commonInfoJSON(typeName: string, typeNo: number): CommonInfoData[] {
    let itemList: marietta.webcad.item.MonicaObject[];
    if (typeName === 'fitting') {
      itemList = marietta.webcad.WebCADGlobal.itemManager.getItemListByMode(ITEM_MODE_NO.MODE_FITTINGDOWN1_NO);
      itemList = itemList.concat(marietta.webcad.WebCADGlobal.itemManager.getItemListByMode(typeNo));
    } else {
      itemList = marietta.webcad.WebCADGlobal.itemManager.getItemListByMode(typeNo);
    }
    // JSON
    const typeNode: CommonInfoData[] = [];
    let info: marietta.webcad.collection.ItemInfoCollection;

    for (let i: number = 0, len = itemList.length; i < len; i += 1) {
      info = itemList[i].itemInfo;

      const itemNode: CommonInfoData = <CommonInfoData>{};
      itemNode.index = i + 1;
      itemNode.id = info.id;
      itemNode.f = info.floor;
      itemNode.x = info.x;
      itemNode.y = -(info.y - marietta.webcad.WebCADGlobal.LAYER_SIZE_H);
      if (marietta.webcad.WebCADGlobal.webcad2Dmanager.buildingType === BUILDING_TYPE_MANSION) {
        itemNode.x = marietta.webcad.util.WebCADUtil.convertPixToMM(info.x);
        itemNode.y = -(marietta.webcad.util.WebCADUtil.convertPixToMM(info.y) - marietta.webcad.util.WebCADUtil.convertPixToMM(marietta.webcad.WebCADGlobal.LAYER_SIZE_H));
      }
      itemNode.rotation = (info.rotation < 0) ? info.rotation + 360 : info.rotation;
      itemNode.is_reverse = info.lr;
      itemNode.idthreed = info.idthreed;
      itemNode.guid = info.guid;
      itemNode.is_ceiling_item = info.is_ceiling_item;
      if (info.end_item_info) {
        itemNode.end_item_info = info.end_item_info;
      }
      if (info.white_item_info) {
        itemNode.white_item_info = info.white_item_info;
      }

      // 建具は高さ情報・取り付け高さ
      if (typeName === 'fitting') {
        itemNode.width = Math.abs(info.width);
        if (marietta.webcad.WebCADGlobal.webcad2Dmanager.buildingType === BUILDING_TYPE_MANSION) {
          itemNode.width = Math.abs(info.widthmm);
        }
        itemNode.width_mm = Math.abs(info.widthmm).toString();
        if (info.use_mm) {
          itemNode.use_mm = info.use_mm;
        }
        // CAD APIのための情報
        itemNode.auto_height = info.auto_height;
        itemNode.z = info.z ? Math.abs(info.z) : 0;
        itemNode.height = info.fitting_height ? Math.abs(info.fitting_height) : 0.0;
        itemNode.window_frame = 0;
        itemNode.type = 0;

        if( marietta.webcad.WebCADGlobal.isCapableOfHavingEndItemFitting( itemNode.id ) && info.end_item_info){
          itemNode.end_item_info = info.end_item_info;
          const materialNode: RoomMaterialData = <RoomMaterialData>{};
          materialNode.wall = info.material.wall ? info.material.wall : null;
          itemNode.material = materialNode;
        }
      } else {
        itemNode.z = info.z ? info.z : 0;
        itemNode.depth = Math.abs(info.heightmm);
        itemNode.width = Math.abs(info.widthmm);
        itemNode.height = info.depth ? Math.abs(info.depth) : 0.0;
      }
      itemNode.text = info.text ? info.text : '';
      itemNode.rotation = (info.rotation < 0) ? info.rotation + 360 : info.rotation;
      itemNode.documents = info.documents ?? [];
      typeNode.push(itemNode);
    }
    return typeNode;
  }

  /**
  * 階段ノード
  */
  static stairInfoJSON(): StairInfoData[] {
    let info: marietta.webcad.collection.ItemInfoCollection;
    const itemList: marietta.webcad.item.Stair[] = <marietta.webcad.item.Stair[]>marietta.webcad.WebCADGlobal.itemManager.getItemListByMode(ITEM_MODE_NO.MODE_STAIR_NO);
    const stairNode: StairInfoData[] = [];
    for (let i = 0, len = itemList.length; i < len; i += 1) {
      info = itemList[i].itemInfo;
      // アイテム
      const itemNode: StairInfoData = <StairInfoData>{};
      itemNode.width = Math.abs(info.width);
      itemNode.depth = Math.abs(info.height);
      itemNode.x = info.x;
      itemNode.y = -(info.y - marietta.webcad.WebCADGlobal.LAYER_SIZE_H);
      itemNode.id = info.id;
      itemNode.index = i + 1;
      itemNode.f = info.floor;
      itemNode.rotation = (info.rotation < 0) ? info.rotation + 360 : info.rotation;
      itemNode.is_reverse = info.lr;
      itemNode.idthreed = info.idthreed;
      itemNode.guid = info.guid;
      // シェイプ
      itemNode.shape = info.getPointJSON(ITEM_MODE_KEY.MODE_STAIR_KEY);
      stairNode.push(itemNode);
    }
    return stairNode;
  }

  /**
  * 屋根情報ノード
  */
  static roofInfoJSON<T extends CommonItemInfoData & { type: number; direction?: number; }>(): RoofInfoData<T> {
    let info: marietta.webcad.collection.ItemInfoCollection;
    const itemList: marietta.webcad.item.Roof[] = <marietta.webcad.item.Roof[]>marietta.webcad.WebCADGlobal.itemManager.getItemListByMode(ITEM_MODE_NO.MODE_ROOF_NO);
    const roofNode: RoofInfoData<T> = <RoofInfoData<T>>{};
    roofNode.noki_de = marietta.webcad.WebCADGlobal.webcad2Dmanager.noki_de;
    roofNode.tsuma_de = marietta.webcad.WebCADGlobal.webcad2Dmanager.tsuma_de;
    roofNode.items = [];

    for (let i = 0, len = itemList.length; i < len; i += 1) {
      info = itemList[i].itemInfo;
      // アイテム
      const itemNode: T = <T>{};
      itemNode.id = info.id;
      itemNode.guid = info.guid;
      itemNode.index = (i + 1);
      itemNode.f = info.floor;
      itemNode.type = info.roof_type;
      if (info.roof_type === 2) {
        itemNode.direction = info.roof_direction;
      }
      // シェイプ
      itemNode.shape = info.getPointJSON(ITEM_MODE_KEY.MODE_ROOF_KEY);
      roofNode.items.push(itemNode);
    }
    return roofNode;
  }

  /**
  * 階段ノード
  */
  static freeTextInfoJSON(): FreeTextInfoData[] {
    let info: marietta.webcad.collection.ItemInfoCollection;
    const itemList: marietta.webcad.item.FreeText[] = <marietta.webcad.item.FreeText[]>marietta.webcad.WebCADGlobal.itemManager.getItemListByMode(ITEM_MODE_NO.MODE_FREETEXT_NO);
    const textNode: FreeTextInfoData[] = [];
    for (let i = 0, len = itemList.length; i < len; i += 1) {
      info = itemList[i].itemInfo;
      // アイテム
      const itemNode: FreeTextInfoData = <FreeTextInfoData>{};
      itemNode.width = Math.abs(parseInt(info.width.toString(), 10));
      itemNode.height = Math.abs(parseInt(info.height.toString(), 10));
      itemNode.x = info.x;
      itemNode.y = -(info.y - marietta.webcad.WebCADGlobal.LAYER_SIZE_H);
      itemNode.z = info.z ? info.z : 0;
      if (marietta.webcad.WebCADGlobal.webcad2Dmanager.buildingType === BUILDING_TYPE_MANSION) {
        itemNode.width = Math.abs(parseInt(info.widthmm.toString(), 10));
        itemNode.height = Math.abs(parseInt(info.heightmm.toString(), 10));
        itemNode.x = marietta.webcad.util.WebCADUtil.convertPixToMM(info.x);
        itemNode.y = -(marietta.webcad.util.WebCADUtil.convertPixToMM(info.y) - marietta.webcad.util.WebCADUtil.convertPixToMM(marietta.webcad.WebCADGlobal.LAYER_SIZE_H));
      }
      itemNode.id = info.id;
      itemNode.index = i + 1;
      itemNode.f = info.floor;
      itemNode.rotation = (info.rotation < 0) ? info.rotation + 360 : info.rotation;
      itemNode.transformMatrix = info.transformMatrix;
      itemNode.guid = info.guid;
      itemNode.text = info.text;
      itemNode.text_color = info.text_color;
      itemNode.font_size = info.font_size;
      itemNode.is_bold = info.is_bold;
      itemNode.is_underline = info.is_underline;
      itemNode.is_italic = info.is_italic;
      itemNode.is_strike_through = info.is_strike_through;
      if (info.link) {
        itemNode.link = info.link;
      }
      if (info.after_link) {
        itemNode.after_link = info.after_link;
      }
      if (info.remarks) {
        itemNode.remarks = info.remarks;
      }
      if (info.file_id) {
        itemNode.file_id = info.file_id;
      }
      if (info.pin_id !== null && info.pin_id !== undefined) itemNode.pin_id = info.pin_id;
      if (info.is_register_img !== null && info.is_register_img !== undefined) itemNode.is_register_img = info.is_register_img;
      if (info.auxiliaryData) {
        itemNode.auxiliaryData = info.auxiliaryData;
      }
      if( info.intersectItem ){
        itemNode.intersectItem = {uid:info.intersectItem.uid, type:info.intersectItem.type, linkedAt:info.intersectItem.linkedAt};
      }

      textNode.push(itemNode);
    }
    return textNode;
  }

  /**
  * 柱情報ノード
  */
  static columnInfoJSON(list: marietta.webcad.item.Column[] = null): CommonInfoData[] {
    let itemList: marietta.webcad.item.Column[] = marietta.webcad.WebCADGlobal.itemManager.getItemListByMode(ITEM_MODE_NO.MODE_COLUMN_NO) as marietta.webcad.item.Column[];
    if (list) {
      itemList = list;
    }
    const columnNode: CommonInfoData[] = [];
    for (let i: number = 0, len = itemList.length; i < len; i += 1) {
      const info: marietta.webcad.collection.ItemInfoCollection = itemList[i].itemInfo;
      // アイテム
      const itemNode:CommonInfoData = <CommonInfoData>{};
      itemNode.index = (i + 1);
      itemNode.id = info.id;
      itemNode.f = info.floor;
      itemNode.guid = info.guid;
      itemNode.shape = info.getPointJSON(ITEM_MODE_KEY.MODE_COLUMN_KEY); // シェイプ
      columnNode.push(itemNode);
    }
    return columnNode;
  }

  /**
  * 梁情報ノード
  */
  static beamInfoJSON(list: marietta.webcad.item.Beam[] = null): CommonInfoData[] {
    let itemList: marietta.webcad.item.Beam[] = marietta.webcad.WebCADGlobal.itemManager.getItemListByMode(ITEM_MODE_NO.MODE_BEAM_NO) as marietta.webcad.item.Beam[];
    if (list) {
      itemList = list;
    }
    const beamNode: CommonInfoData[] = [];
    for (let i: number = 0, len = itemList.length; i < len; i += 1) {
      const info: marietta.webcad.collection.ItemInfoCollection = itemList[i].itemInfo;
      // アイテム
      const itemNode:CommonInfoData = <CommonInfoData>{};
      itemNode.index = (i + 1);
      itemNode.id = info.id;
      itemNode.f = info.floor;
      itemNode.guid = info.guid;
      itemNode.height = info.height;
      itemNode.shape = info.getPointJSON(ITEM_MODE_KEY.MODE_BEAM_KEY); // シェイプ

      itemNode.material = info.material;
      itemNode.end_item_info = info.end_item_info;
      itemNode.documents = info.documents ?? [];
      if (info.white_item_info) itemNode.white_item_info = info.white_item_info;

      beamNode.push(itemNode);
    }
    return beamNode;
  }

  /**
   * 見積データ（WebCAD用)
   */
  static quotationInfoJSON(): QuotationData[] {
    const quortationNode: QuotationData[] = [];
    const { tableDataQuotation } = marietta.webcad.WebCADGlobal.quotationItemList;
    for (let i: number = 0; i < tableDataQuotation.length; i += 1) {
      // アイテム
      const itemNode:QuotationData = <QuotationData>{};
      itemNode.is_quote = tableDataQuotation[i].is_quote;
      itemNode.name = tableDataQuotation[i].name;
      itemNode.guid = tableDataQuotation[i].guid;
      itemNode.floor = tableDataQuotation[i].floor;
      const width = tableDataQuotation[i]?.width;
      itemNode.width = typeof width === 'number' ? width : parseFloat(width);
      const depth = tableDataQuotation[i]?.depth;
      itemNode.depth = typeof depth === 'number' ? depth : parseFloat(depth);
      const height = tableDataQuotation[i]?.height;
      itemNode.height = typeof height === 'number' ? height : parseFloat(height);
      const area = tableDataQuotation[i]?.area;
      itemNode.area = typeof area === 'number' ? area : parseFloat(area);
      itemNode.unit = tableDataQuotation[i].unit;
      itemNode.maker = tableDataQuotation[i].maker;
      itemNode.part_number = tableDataQuotation[i].part_number;
      itemNode.item_name = tableDataQuotation[i].item_name;
      itemNode.item_sub_category = tableDataQuotation[i].item_sub_category;
      itemNode.unit_price = tableDataQuotation[i]?.unit_price.toString(10);
      const price = tableDataQuotation[i]?.price;
      itemNode.price = typeof price === 'string' ? price : price.toString(10);
      itemNode.item_set_date = tableDataQuotation[i].item_set_date;
      itemNode.note = tableDataQuotation[i].note;
      itemNode.room_name = tableDataQuotation[i].room_name;
      itemNode.end_item_info = tableDataQuotation[i].end_item_info;
      itemNode.white_item_info = tableDataQuotation[i].white_item_info;
      itemNode.documents = tableDataQuotation[i].documents;
      itemNode.auxiliary_constructions = AuxiliaryUtil.getAuxiliaryListForQuoteById( tableDataQuotation[i].guid );

      if( !itemNode.auxiliary_constructions ){
        console.log(itemNode.auxiliary_constructions);
      }

      // itemNode.unit = tableDataQuotation[i].unit;
      // itemNode.price = tableDataQuotation[i].price;
      // itemNode.maker = tableDataQuotation[i].maker;
      // itemNode.part_number = tableDataQuotation[i].part_number;
      // itemNode.note = tableDataQuotation[i].note;
      // itemNode.room_name = tableDataQuotation[i].room_name;
      // itemNode.item_name = tableDataQuotation[i].item_name;
      // itemNode.item_id = tableDataQuotation[i].item_id;

      quortationNode.push(itemNode);
    }
    return quortationNode;
  }

  /**Plan
   * 見積データ（サイト用）
   */
  static quotationInfoJSONForSite(): QuotationDataForSite[] {
    const quortationNode: QuotationDataForSite[] = [];
    const { tableDataQuotation } = marietta.webcad.WebCADGlobal.quotationItemList;
    let count:number = 0;
    for (let i: number = 0; i < tableDataQuotation.length; i += 1) {
      const hasAuxiliaryConstructions = AuxiliaryUtil.getAuxiliaryListForQuoteById( tableDataQuotation[i].guid ); // 付帯工事
      const hasAuxiliaryWithCheckOn = AuxiliaryUtil.hasAuxiliaryWithCheckOn(tableDataQuotation[i].guid); // 付帯工事

      //親が見積参入なしで、付帯工事がない場合はスキップ
      if( !tableDataQuotation[i].is_quote && !hasAuxiliaryWithCheckOn) continue;

      //付帯工事の場合は、出力方式が異なるので、一覧で来た場合は、無視をする。
      //※親アイテムの方で処理をするので、不要
      if( tableDataQuotation[i].is_auxiliary_data ){
        //紐づけアイテムがなくって、見積参入したい場合
        if( !tableDataQuotation[i].parentPin.itemInfo.intersectItem && tableDataQuotation[i].is_quote ){
          //親工事がOFFで付帯工事として連携する場合は、空工事とする
          // 空工事アイテムとして作成をする
          const itemNode = this.createEmptyAuxiliaryDataForUnlinkedPin(tableDataQuotation[i].guid, count++);
          quortationNode.push(itemNode);
        }
        continue;
      }

      //見積参入
      if( tableDataQuotation[i].is_quote ){
        // アイテム
        const itemNode:QuotationDataForSite = <QuotationDataForSite>{};
        itemNode.item_id = tableDataQuotation[i].item_id;
        itemNode.construction_code = marietta.webcad.WebCADGlobal.masterManager.getConstructionCodeForID( tableDataQuotation[i]?.end_item_info?.symbol_2d_code ); // 共通工事コード
        const unit_price = tableDataQuotation[i]?.unit_price;
        itemNode.unit_price = typeof unit_price === 'number' ? unit_price : parseFloat(unit_price);
        itemNode.guid = tableDataQuotation[i].guid;
        itemNode.order = count++;
        itemNode.room_name = tableDataQuotation[i].room_name?tableDataQuotation[i].room_name:'';
        itemNode.part_number = tableDataQuotation[i].part_number;
        itemNode.item_name = tableDataQuotation[i].item_name;
        itemNode.quantity = parseFloat(tableDataQuotation[i].area);
        itemNode.unit = tableDataQuotation[i].unit;
        itemNode.note = tableDataQuotation[i].note;
        itemNode.documents = tableDataQuotation[i].documents;

        //付帯工事
        if (hasAuxiliaryConstructions.length > 0 && !AuxiliaryUtil.hasAuxiliaryWithCheckOn(tableDataQuotation[i].guid)){
          itemNode.auxiliary_constructions = null;
        }else{
          itemNode.auxiliary_constructions = AuxiliaryUtil.getAuxiliaryListForQuoteById( tableDataQuotation[i].guid );
        }

        itemNode.maker = tableDataQuotation[i].maker;
        itemNode.item_sub_category = tableDataQuotation[i].item_sub_category;
        const price = tableDataQuotation[i]?.price;
        itemNode.price = typeof price === 'number' ? price : parseFloat(price);
        itemNode.webcad_item_name = tableDataQuotation[i]?.name ?? '';
        quortationNode.push(itemNode);
      } else {
        //巾木の場合は、壁のコードと一緒で、ここで引っ掛かってしまうので、無視するようにする。
        //付帯工事が見積参入の場合は、空行追加で対処したいが、巾木も同じIDでひっかかるので、無視をして対応をする
        if( tableDataQuotation[i].isHabaki ) continue;

        //親工事がOFFで付帯工事として連携する場合は、空工事とする
        // 空工事アイテムとして作成をする
        const itemNode = this.createEmptyAuxiliaryData(tableDataQuotation[i].guid, count++);
        quortationNode.push(itemNode);
      }
    }
    return quortationNode;
  }

  /**
   * 付帯工事用　空オブジェクトを作成する(共通部分)
   * @param order
   * @param auxiliaryConstructions 空オブジェクトに紐付ける付帯工事一覧
   */
  private static buildEmptyAuxiliaryDataNode( order:number, auxiliaryConstructions: AuxiliaryConstructionCollection[] ):QuotationDataForSite{
    const itemNode:QuotationDataForSite = <QuotationDataForSite>{};
    itemNode.item_id = null;
    itemNode.guid = null;
    itemNode.part_number = null;
    itemNode.item_name = null;
    itemNode.quantity = null;
    itemNode.unit = null;
    itemNode.unit_price = null;
    itemNode.price = null;
    itemNode.note = null;
    itemNode.maker = null;
    itemNode.item_sub_category = null;
    itemNode.construction_code = null;
    itemNode.documents = null;
    itemNode.webcad_item_name = null;
    itemNode.order = order;
    itemNode.auxiliary_constructions = auxiliaryConstructions;
    itemNode.room_name = itemNode?.auxiliary_constructions[0]?.room_name??'';
    return itemNode;
  }

  /**
   * 付帯工事用　空オブジェクトを作成する
   * 親工事(紐づけアイテム)がOFFで、付帯工事のみ見積参入する場合に使用する
   * @param guid 紐づけアイテム(親工事)側のguid
   * @param order
   */
  static createEmptyAuxiliaryData( guid:string, order:number ):QuotationDataForSite{
    return this.buildEmptyAuxiliaryDataNode(order, AuxiliaryUtil.getAuxiliaryListForQuoteById(guid));
  }

  /**
   * 付帯工事用　空オブジェクトを作成する
   * 紐づけアイテムが削除された付帯工事自身を、見積参入する場合に使用する
   * @param pinGuid 付帯工事(ピン)自身のguid
   * @param order
   */
  static createEmptyAuxiliaryDataForUnlinkedPin( pinGuid:string, order:number ):QuotationDataForSite{
    return this.buildEmptyAuxiliaryDataNode(order, AuxiliaryUtil.getAuxiliaryByGuid(pinGuid));
  }

  /**
  * 画像検知ノード
  */
  static markInfoJSON(): MarkItemInterface[] {
    let info: marietta.webcad.collection.ItemInfoCollection;
    const itemList: marietta.webcad.item.Mark[] = <marietta.webcad.item.Mark[]>marietta.webcad.WebCADGlobal.itemManager.getItemListByMode(ITEM_MODE_NO.MODE_MARK_NO);
    const markNode: MarkItemInterface[] = [];
    for (let i = 0, len = itemList.length; i < len; i += 1) {
      info = itemList[i].itemInfo;
      // アイテム
      const itemNode: MarkItemInterface = <MarkItemInterface>{};
      itemNode.width = Math.abs(parseInt(info.width.toString(), 10));
      itemNode.height = Math.abs(parseInt(info.height.toString(), 10));
      itemNode.x = info.x;
      itemNode.y = -(info.y - marietta.webcad.WebCADGlobal.LAYER_SIZE_H);
      itemNode.z = info.z ? info.z : 0;
      if (marietta.webcad.WebCADGlobal.webcad2Dmanager.buildingType === BUILDING_TYPE_MANSION) {
        itemNode.width = Math.abs(parseInt(info.widthmm.toString(), 10));
        itemNode.height = Math.abs(parseInt(info.heightmm.toString(), 10));
        itemNode.x = marietta.webcad.util.WebCADUtil.convertPixToMM(info.x);
        itemNode.y = -(marietta.webcad.util.WebCADUtil.convertPixToMM(info.y) - marietta.webcad.util.WebCADUtil.convertPixToMM(marietta.webcad.WebCADGlobal.LAYER_SIZE_H));
      }
      itemNode.id = info.id;
      itemNode.index = i + 1;
      itemNode.f = info.floor;
      itemNode.rotation = (info.rotation < 0) ? info.rotation + 360 : info.rotation;
      itemNode.transformMatrix = info.transformMatrix;
      itemNode.guid = info.guid;
      if (info.link) {
        itemNode.link = info.link;
      }
      itemNode.recognizeData = info.recognizeData;
      markNode.push(itemNode);
    }
    return markNode;
  }

}
